import { Flex } from "@/components/atoms/flex";
import { useAllInteractionsStyle } from "@/components/templates/allInteractions/style";
import {
	mergeClasses,
	TableCell,
	TableRow,
	Accordion,
	AccordionItem,
	AccordionHeader,
	AccordionPanel,
	AccordionToggleEventHandler,
} from "@fluentui/react-components";
import { SupervisorTableHeader } from "@/components/molecules/supervisor";
import { useInteractionStyle } from "@/components/organisms/interaction/style";
import {
	TableType,
	waitingInteractionsTableColumnDefinitions,
	getWaitingTableColumnInfo,
	getWaitingTableColumnWidth,
	WaitingInteractionsTableColumnNames,
} from "@/common/types";
import { TagStack } from "@/components/atoms/tagStack";
import { useTagsExist } from "@/common/stores/persistingStores/app/appSettingsStore";
import { InteractionDto, InteractionHandlingStatus } from "@/repository/models";
import { useTableStyle } from "@/components/atoms/customTable";
import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { TooltipEllipsisTableCell } from "@/components/atoms/tooltipEllipsisTableCell";
import { SkillTableCell } from "@/components/atoms/skillTableCell";
import { CustomerTableCell } from "@/components/atoms/customerTableCell";
import { useIntl } from "react-intl";
import {
	mapInteractionStatusToPanelStatus,
	getInteractionName,
} from "@/common/helpers";
import { getInteractionSecondaryInfo } from "@/common/helpers/supervisorHelper";
import { useStyle } from ".";
import { InteractionState } from "@/components/organisms/interaction/types";
import useResizeListener from "@/hooks/window";
import {
	InteractionFilterState,
	useInteractionFilterStore,
} from "@/common/stores/nonpersistingStores/interactionfilter";
import { CustomStaticTable } from "@/components/atoms/customTable/CustomStaticTable";
import { InteractionIconOverlay } from "@/components/molecules/interactionIconOverlay";
import { useAgentInteractionState } from "@/hooks/agent";
import {
	useWaitingInteractions,
	useWaitingInteractionActions,
} from "@/hooks/allInteractions";
import {
	useQueuesStore,
	QueuesState,
} from "@/common/stores/nonpersistingStores/queues";
import { SparkleFilled } from "@fluentui/react-icons";

export const AllWaitingInteractionsTable = () => {
	const intl = useIntl();

	const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);
	const [waitingDivKey, setWaitingDivKey] = useState<string | null>(
		`waiting-${Math.random()}`
	);

	const filterData = useInteractionFilterStore(
		(state: InteractionFilterState) => state.waiting
	);
	const [openItems, setOpenItems] = useState([InteractionState.Waiting]);

	const classes = useStyle();
	const interactionClasses = useInteractionStyle();
	const tableClasses = useTableStyle();
	const allInteractionsStyleClasses = useAllInteractionsStyle();

	const waitingSectionMaxHeight = "calc(100vh - 251px)";
	const [waitingSectionHeight, setWaitingSectionHeight] = useState<string>(
		waitingSectionMaxHeight
	);
	const [voicemailSectionHeight, setVoicemailSectionHeight] =
		useState<string>("0px");
	const waitingElementRef = useRef<HTMLDivElement>(null);
	const resizeListener = useResizeListener();

	const { agentHasCallInProgressOrTextInOffering } = useAgentInteractionState();
	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const toggleInteractionCategoryVisibilityHandler: AccordionToggleEventHandler<
		InteractionState
	> = (_, data) => {
		toggleInteractionCategoryVisibility(data.value, data.openItems);
	};

	const toggleInteractionCategoryVisibility = (
		value: InteractionState,
		openInteractionItems: InteractionState[]
	) => {
		let heightToSet;

		if (value === InteractionState.Waiting) {
			if (!openInteractionItems.includes(InteractionState.Waiting)) {
				heightToSet = "0px";
			} else {
				heightToSet = openInteractionItems.includes(InteractionState.Voicemail)
					? "45vh"
					: waitingSectionMaxHeight;
			}
		} else {
			if (openInteractionItems.includes(InteractionState.Waiting)) {
				heightToSet = openInteractionItems.includes(InteractionState.Voicemail)
					? "45vh"
					: waitingSectionMaxHeight;
			} else {
				heightToSet = openInteractionItems.includes(InteractionState.Voicemail)
					? "0px"
					: waitingSectionMaxHeight;
			}
		}

		setOpenItems(openInteractionItems);
		setWaitingSectionHeight(heightToSet);
		setTimeout(() => {
			setWaitingDivKey(`waiting${Math.random()}`);
		}, 100);
	};
	const {
		waitingInteractions,
		nonVoicemailInteractions,
		voicemailInteractions,
	} = useWaitingInteractions(() => {
		toggleInteractionCategoryVisibility(InteractionState.Waiting, [
			InteractionState.Waiting,
		]);
	});

	const hasAnyQueueSkillRoutingEnabled = useMemo(() => {
		const waitingQueueIds = new Set(waitingInteractions?.map((x) => x.queueId));

		return queues.some(
			(q) => waitingQueueIds.has(q.id) && q.isSkillBasedRoutingEnabled
		);
	}, [waitingInteractions, queues]);

	const tagsExist = useTagsExist();

	// Memoized on the two flags the widths derive from, so columnWidth — and the row renderer that
	// depends on it — stay referentially stable across the frequent re-renders this table sees.
	const waitingTableColumnInfo = useMemo(
		() => getWaitingTableColumnInfo(hasAnyQueueSkillRoutingEnabled, tagsExist),
		[hasAnyQueueSkillRoutingEnabled, tagsExist]
	);

	const columnWidth = useCallback(
		(name: WaitingInteractionsTableColumnNames) =>
			getWaitingTableColumnWidth(waitingTableColumnInfo, name),
		[waitingTableColumnInfo]
	);

	const { renderTimerTableCell } = useWaitingInteractionActions();

	const queuesWithSkillsBasedRoutingEnabled = useMemo(() => {
		return new Set(
			queues.filter((x) => x.isSkillBasedRoutingEnabled).map((x) => x.id)
		);
	}, [queues]);

	const getWaitingRow = useCallback(
		(item: InteractionDto) => (
			<TableRow
				key={item.id}
				className={mergeClasses(tableClasses.row, tableClasses.bodyText)}
				onMouseLeave={() => setHoveredItemKey(null)}
				onMouseEnter={() => setHoveredItemKey(item.id)}
			>
				<TableCell
					style={{
						width: columnWidth(WaitingInteractionsTableColumnNames.Customer),
					}}
				>
					<CustomerTableCell
						icon={
							<InteractionIconOverlay
								interaction={item}
								itemPanelStatus={mapInteractionStatusToPanelStatus(item.status)}
								interactionHandlingStatus={InteractionHandlingStatus.Waiting}
								hideDirectionArrow
								isSkillBasedRoutingEnabled={queuesWithSkillsBasedRoutingEnabled.has(
									item.queueId
								)}
							/>
						}
						name={getInteractionName(item.type, item.name, intl)}
						contactInformation={getInteractionSecondaryInfo(item, intl)}
						direction={item.direction}
						displayAvatar={true}
					/>
				</TableCell>
				<TooltipEllipsisTableCell
					text={item.queueName}
					tableCellStyle={{
						width: columnWidth(WaitingInteractionsTableColumnNames.Queue),
					}}
				/>
				{hasAnyQueueSkillRoutingEnabled && (
					<TableCell
						style={{
							width: columnWidth(WaitingInteractionsTableColumnNames.Skills),
						}}
					>
						<SkillTableCell skills={item.skills} />
					</TableCell>
				)}
				{tagsExist && (
					<TableCell
						style={{
							width: columnWidth(WaitingInteractionsTableColumnNames.Tags),
						}}
					>
						<TagStack tags={item.assignedTags} />
					</TableCell>
				)}
				<TooltipEllipsisTableCell
					text={item.agentName?.length ? item.agentName : "-"}
					tableCellStyle={{
						width: columnWidth(WaitingInteractionsTableColumnNames.Agent),
					}}
					suffixIcon={item.isHandledByAiAgent ? <SparkleFilled /> : undefined}
				/>
				<TableCell
					style={{
						width: columnWidth(WaitingInteractionsTableColumnNames.Timer),
					}}
					className={tableClasses.timer}
				>
					{renderTimerTableCell(item, hoveredItemKey)}
				</TableCell>
			</TableRow>
		),
		[
			hoveredItemKey,
			renderTimerTableCell,
			agentHasCallInProgressOrTextInOffering,
			columnWidth,
			hasAnyQueueSkillRoutingEnabled,
			tagsExist,
		]
	);

	useEffect(() => {
		const waitingElement = waitingElementRef.current;
		setVoicemailSectionHeight(
			`calc(100vh - 208.5px - ${waitingElement?.offsetHeight ?? "0"}px)`
		);
	}, [waitingSectionHeight, waitingInteractions, resizeListener]);

	return (
		<Flex
			className={mergeClasses(
				allInteractionsStyleClasses.leftTable,
				interactionClasses.waitingBackground
			)}
			column
		>
			<SupervisorTableHeader
				type={TableType.None}
				count={
					(nonVoicemailInteractions?.length ?? 0) +
					(voicemailInteractions?.length ?? 0)
				}
				label={intl.formatMessage({
					id: "Interaction.Contents.WaitingAndVoicemailInteractions",
				})}
				filterData={filterData}
			/>

			{nonVoicemailInteractions ? (
				<CustomStaticTable
					items={nonVoicemailInteractions}
					columnInfo={waitingTableColumnInfo}
					columnDefinitions={waitingInteractionsTableColumnDefinitions}
				/>
			) : (
				<></>
			)}

			<Accordion
				multiple
				collapsible
				openItems={openItems}
				onToggle={toggleInteractionCategoryVisibilityHandler}
			>
				<AccordionItem
					value={InteractionState.Waiting}
					className={interactionClasses.waitingAccordion}
				>
					<AccordionHeader
						size="small"
						className={interactionClasses.accordionHeader}
					>
						<SupervisorTableHeader
							type={TableType.Waiting}
							count={nonVoicemailInteractions?.length ?? 0}
							filterData={filterData}
						/>
					</AccordionHeader>
					<AccordionPanel
						ref={waitingElementRef}
						style={{ margin: 0 }}
						key={waitingDivKey}
					>
						<div
							style={{
								maxHeight: waitingSectionHeight,
								scrollbarGutter: "stable both-edges",
								overflowY:
									nonVoicemailInteractions?.length &&
									nonVoicemailInteractions?.length < 5
										? undefined
										: "auto",
							}}
						>
							{nonVoicemailInteractions ? (
								<CustomStaticTable
									items={nonVoicemailInteractions}
									columnInfo={waitingTableColumnInfo}
									columnDefinitions={waitingInteractionsTableColumnDefinitions}
									getRowTemplateCallback={getWaitingRow}
									hideHeaders
								/>
							) : (
								<></>
							)}
						</div>
					</AccordionPanel>
				</AccordionItem>

				<AccordionItem value={InteractionState.Voicemail}>
					<AccordionHeader
						size="small"
						className={interactionClasses.accordionHeader}
					>
						<SupervisorTableHeader
							type={TableType.Voicemail}
							count={voicemailInteractions?.length ?? 0}
							filterData={filterData}
						/>
					</AccordionHeader>
					<AccordionPanel
						className={mergeClasses(
							interactionClasses.accordionPanel,
							classes.overflowAuto
						)}
						style={{
							height: voicemailSectionHeight,
							margin: 0,
						}}
					>
						{voicemailInteractions ? (
							<CustomStaticTable
								items={voicemailInteractions}
								columnInfo={waitingTableColumnInfo}
								columnDefinitions={waitingInteractionsTableColumnDefinitions}
								getRowTemplateCallback={getWaitingRow}
								hideHeaders
							/>
						) : (
							<></>
						)}
					</AccordionPanel>
				</AccordionItem>
			</Accordion>
		</Flex>
	);
};
